import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { labelsService, FetchLabelDeploymentCoverageParams } from '../services/labels.service';
import type {
  LabelDeploymentCoverageResponse,
  LabelEntity,
  LabelDeploymentStatus,
} from '../services/types';
import type { RootState } from '../store';
import type { TrackedDeployment } from '../services/types';
import { TERMINAL_DEPLOYMENT_STATUSES, DEPLOYMENT_TRACKING_STORAGE_KEY } from '../services/types';

// Local UI deployment status — adds in-flight states tracked client-side.
export type LabelUIStatus =
  | 'Deployed'
  | 'NotDeployed'
  | 'InProgress'
  | 'DeploymentFailed';

interface LabelDeploymentState {
  // Data
  entities: LabelEntity[];
  baseTenantId: string;
  baseTenantName: string;
  totalTenants: number;

  // Pagination
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;

  // View context — which tenant ids the current page was fetched for.
  // Empty array means dashboard view (all authorized tenants).
  currentTargetTenantIds: string[];

  // Loading & Error states
  loading: boolean;
  error: string | null;

  // Per-(entityId, tenantId) UI status overrides (in-flight / failed deploys
  // tracked client-side, layered on top of server-supplied steady state).
  uiStatusOverrides: Record<string, LabelUIStatus>;

  lastFetched: string | null;
}

const initialState: LabelDeploymentState = {
  entities: [],
  baseTenantId: '',
  baseTenantName: '',
  totalTenants: 0,
  pageNumber: 1,
  pageSize: 25,
  totalCount: 0,
  totalPages: 0,
  hasNextPage: false,
  currentTargetTenantIds: [],
  loading: false,
  error: null,
  uiStatusOverrides: {},
  lastFetched: null,
};

const overrideKey = (entityId: string, tenantId: string): string =>
  `${entityId.toLowerCase()}::${tenantId.toLowerCase()}`;

/**
 * Read active tracked LABEL deployments from localStorage and compute
 * uiStatusOverrides entries for matching entities.
 * Called from the thunk (not the reducer) — same pattern as
 * tenantPoliciesSlice.rehydrateTenantInProgressStatuses.
 */
function computeInFlightLabelOverrides(
  entities: LabelEntity[],
  targetTenantIds: string[],
): Record<string, LabelUIStatus> {
  const result: Record<string, LabelUIStatus> = {};
  try {
    const raw = localStorage.getItem(DEPLOYMENT_TRACKING_STORAGE_KEY);
    if (!raw) return result;
    const items: TrackedDeployment[] = JSON.parse(raw);
    const entityGuids = new Set(entities.map(e => e.entityId.toLowerCase()));
    const targetSet = new Set(targetTenantIds.map(t => t.toLowerCase()));
    items.forEach(item => {
      if (
        item.entityType === 'LABEL' &&
        !TERMINAL_DEPLOYMENT_STATUSES.has(item.apiStatus) &&
        (targetSet.size === 0 || targetSet.has(item.tenantId.toLowerCase())) &&
        entityGuids.has(item.entityId.toLowerCase())
      ) {
        result[overrideKey(item.entityId, item.tenantId)] = 'InProgress';
      }
    });
  } catch { /* ignore malformed storage */ }
  return result;
}

type FetchResult = LabelDeploymentCoverageResponse & {
  inFlightOverrides: Record<string, LabelUIStatus>;
};

export const fetchLabelDeploymentCoverage = createAsyncThunk<
  FetchResult,
  FetchLabelDeploymentCoverageParams,
  { rejectValue: string }
>('labelDeployment/fetch', async (params, { rejectWithValue }) => {
  try {
    const response = await labelsService.fetchDeploymentCoverage(params);
    const inFlightOverrides = computeInFlightLabelOverrides(
      response.entities,
      params.targetTenantIds ?? [],
    );
    return { ...response, inFlightOverrides };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to fetch labels';
    return rejectWithValue(message);
  }
});

const slice = createSlice({
  name: 'labelDeployment',
  initialState,
  reducers: {
    clearLabels: () => initialState,

    setPage: (state, action: PayloadAction<number>) => {
      state.pageNumber = Math.max(1, action.payload);
    },

    /** Mark a (label, tenant) pair as in-flight after a deploy is dispatched. */
    setDeploymentInFlight: (
      state,
      action: PayloadAction<{ entityId: string; tenantIds: string[] }>,
    ) => {
      const { entityId, tenantIds } = action.payload;
      for (const tid of tenantIds) {
        state.uiStatusOverrides[overrideKey(entityId, tid)] = 'InProgress';
      }
    },

    /** Roll back optimistic InProgress overrides when a deploy API call fails. */
    clearOverridesForEntities: (
      state,
      action: PayloadAction<{ entityId: string; tenantIds: string[] }[]>,
    ) => {
      for (const { entityId, tenantIds } of action.payload) {
        for (const tid of tenantIds) {
          delete state.uiStatusOverrides[overrideKey(entityId, tid)];
        }
      }
    },

    /** Apply a single status update from deploy polling. */
    applyDeploymentStatus: (
      state,
      action: PayloadAction<{
        entityId: string;
        tenantId: string;
        status: LabelUIStatus;
      }>,
    ) => {
      const { entityId, tenantId, status } = action.payload;
      const key = overrideKey(entityId, tenantId);
      if (status === 'Deployed' || status === 'NotDeployed') {
        // Terminal success/idle — also update the canonical server-supplied row
        const entity = state.entities.find(
          e => e.entityId.toLowerCase() === entityId.toLowerCase(),
        );
        if (entity) {
          const td = entity.tenantDeployments.find(
            t => t.tenantId.toLowerCase() === tenantId.toLowerCase(),
          );
          if (td) {
            td.deploymentStatus = status as LabelDeploymentStatus;
          }
        }
        delete state.uiStatusOverrides[key];
      } else {
        state.uiStatusOverrides[key] = status;
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchLabelDeploymentCoverage.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLabelDeploymentCoverage.fulfilled, (state, action) => {
        const r = action.payload;
        state.loading = false;
        state.entities = r.entities;
        state.baseTenantId = r.baseTenantId;
        state.baseTenantName = r.baseTenantName;
        state.totalTenants = r.totalTenants;
        state.pageNumber = r.pagination.pageNumber;
        state.pageSize = r.pagination.pageSize;
        state.totalCount = r.pagination.totalCount;
        state.totalPages = r.pagination.totalPages;
        state.hasNextPage = r.pagination.hasNextPage;
        state.currentTargetTenantIds = action.meta.arg.targetTenantIds ?? [];
        // Merge rehydrated InProgress overrides — same pattern as tenantPoliciesSlice
        Object.assign(state.uiStatusOverrides, r.inFlightOverrides);
        state.lastFetched = new Date().toISOString();
      })
      .addCase(fetchLabelDeploymentCoverage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? action.error.message ?? 'Unknown error';
      });
  },
});

export const {
  clearLabels,
  setPage,
  setDeploymentInFlight,
  clearOverridesForEntities,
  applyDeploymentStatus,
} = slice.actions;

export default slice.reducer;

// ========== Selectors ==========

const selectSlice = (state: RootState) => state.labelDeployment;

export const selectLabels = (state: RootState): LabelEntity[] =>
  selectSlice(state).entities;

/**
 * Labels that should appear as table rows: drop parents, label groups and
 * disabled labels. The dropped entries stay in the slice so View Details can
 * resolve a child's parent display name when needed.
 */
export const selectDeployableLabels = (state: RootState): LabelEntity[] =>
  selectSlice(state).entities.filter(
    e => !e.data.isParent && !e.data.isLabelGroup && !e.data.disabled,
  );

export const selectLabelById = (
  state: RootState,
  entityId: string,
): LabelEntity | undefined =>
  selectSlice(state).entities.find(
    e => e.entityId.toLowerCase() === entityId.toLowerCase(),
  );

export const selectLabelsPagination = (state: RootState) => {
  const s = selectSlice(state);
  return {
    pageNumber: s.pageNumber,
    pageSize: s.pageSize,
    totalCount: s.totalCount,
    totalPages: s.totalPages,
    hasNextPage: s.hasNextPage,
  };
};

export const selectLabelsLoading = (state: RootState): boolean =>
  selectSlice(state).loading;

export const selectLabelsError = (state: RootState): string | null =>
  selectSlice(state).error;

export const selectLabelTenantStatus = (
  state: RootState,
  entityId: string,
  tenantId: string,
): LabelUIStatus => {
  const s = selectSlice(state);
  const override = s.uiStatusOverrides[overrideKey(entityId, tenantId)];
  if (override) return override;
  const entity = s.entities.find(
    e => e.entityId.toLowerCase() === entityId.toLowerCase(),
  );
  const td = entity?.tenantDeployments.find(
    t => t.tenantId.toLowerCase() === tenantId.toLowerCase(),
  );
  return td?.deploymentStatus ?? 'NotDeployed';
};
